import ReactDOM from "react-dom";

import mapboxgl, { Marker } from "mapbox-gl";
import cx from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import getCenterBoundry from "geolib/es/getCenterOfBounds";
import getBoundry from "geolib/es/getBounds";
import { lineString } from "@turf/helpers";
import { Feature, LineString, GeoJsonProperties as Properties } from "geojson";
import { toWgs84, toMercator } from "@turf/projection";
import length from "@turf/length";
import midpoint from "@turf/midpoint";
import destination from "@turf/destination";
import bearing from "@turf/bearing";
import lineArc from "@turf/line-arc";
import distance from "@turf/distance";
import { useResponsive } from "@/hooks";
import CustomMarker from "./CustomMarker";
import { ZoomOutControl } from "./ZoomOutControl";
import { NavigationControl } from "./NavigationControl";
import { Coords, TripItinerary } from "@/fetch/inventory";
import { useMapbox } from "./useMapbox";
import { useItineraryMapMarkers } from "./useItineraryMapMarkers";
import styles from "./Mapbox.module.scss";

type TCenter = boolean | { latitude: number; longitude: number };

type AppMapBoxProps = {
  itineries: TripItinerary[];
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  tripLocation: Coords;
};

const AppMapBox = ({
  itineries,
  selectedIndex,
  setSelectedIndex,
  tripLocation,
}: AppMapBoxProps) => {
  const { initMap, map, mapContainer, mapError } = useMapbox();
  const { addStep, markers } = useItineraryMapMarkers(itineries);

  const firstUpdate = useRef(true);

  const { isMobile } = useResponsive();

  const previousIndexRef = useRef<number | null>(null);

  const indexRef = useRef<number>(selectedIndex);
  const isItinerariesInitiated = useRef<boolean>(false);

  const [defaultZoom, setDefaultZoom] = useState(0);

  const getStepsCoords = useCallback(
    (itineriesSteps: TripItinerary[]) => {
      const coords: Array<Coords> = [];
      const steps =
        itineriesSteps?.map((iti) => {
          if (iti.location) {
            coords.push({
              lat:
                parseFloat(iti.location?.[0]?.m_lat) ||
                parseFloat(tripLocation.lat.toString()),
              lng:
                parseFloat(iti.location?.[0]?.m_lng) ||
                parseFloat(tripLocation.lng.toString()),
              zoom: iti.location?.[0]?.m_zoom_level || tripLocation.zoom,
            });
            return {
              lat:
                parseFloat(iti?.location?.[0]?.m_lat) ||
                parseFloat(tripLocation.lat.toString()),
              lng:
                parseFloat(iti?.location?.[0]?.m_lng) ||
                parseFloat(tripLocation.lng.toString()),
              title: iti.title,
            };
          }
          return null;
        }) || [];

      return { steps, coords };
    },
    [tripLocation.lat, tripLocation.lng, tripLocation.zoom]
  );

  const getCenterBoundries = useCallback(
    (
      coords: any
    ): {
      centerLocation: [number, number];
      llb: mapboxgl.LngLatBounds | undefined;
    } => {
      let centerLocation: [number, number] = [
        tripLocation.lat || 0,
        tripLocation.lng || 0,
      ];
      let llb;
      if (coords.length > 0) {
        const centerPoint: TCenter = getCenterBoundry(coords);
        const boundry = getBoundry(coords);
        const sw = new mapboxgl.LngLat(boundry?.maxLng, boundry?.maxLat);
        const ne = new mapboxgl.LngLat(boundry?.minLng, boundry?.minLat);
        llb = new mapboxgl.LngLatBounds(sw, ne);
        if (centerPoint) {
          centerLocation = [centerPoint.longitude, centerPoint.latitude];
        }
      }

      return { centerLocation, llb };
    },
    [tripLocation.lat, tripLocation.lng]
  );

  type StepInfo = {
    lat: number;
    lng: number;
    title: string;
  };

  const createLine = useCallback(
    (from: StepInfo | null, to: StepInfo | null) => {
      if (!from?.lat || !from?.lng || !to?.lat || !to?.lng) {
        return null;
      }
      let route: LineString = {
        type: "LineString",
        coordinates: [
          [from?.lng, from?.lat],
          [to?.lng, to?.lat],
        ],
      };
      let line = lineString([
        [from?.lng, from?.lat],
        [to?.lng, to?.lat],
      ]);
      line = toWgs84(line);
      route = toWgs84(route);
      const lineD = length(line, { units: "kilometers" });

      const mp = midpoint(route?.coordinates?.[0], route?.coordinates?.[1]);
      const center = destination(
        mp,
        lineD,
        bearing(route.coordinates[0], route.coordinates[1]) - 90
      );
      const lA = lineArc(
        center,
        distance(center, route.coordinates[0]),
        bearing(center, route.coordinates[1]),
        bearing(center, route.coordinates[0])
      );

      return toMercator(lA);
    },
    []
  );

  const checkCoordAllSame = (allLocations: Array<Coords>): boolean => {
    return allLocations.every(
      (loc) =>
        loc.lat === allLocations[0].lat && loc.lng === allLocations[0].lng
    );
  };

  const goToNextIndex = () => {
    if (indexRef.current + 1 < itineries.length) {
      indexRef.current = indexRef.current + 1;
      setSelectedIndex(indexRef.current);
    }
  };

  const goToPreviousIndex = () => {
    if (indexRef.current - 1 >= 0) {
      indexRef.current = indexRef.current - 1;
      setSelectedIndex(indexRef.current);
    }
  };

  const getNavigatorStatus = () => {
    return {
      hasNext: indexRef.current + 1 < itineries.length,

      hasPrevious: indexRef.current - 1 >= 0,
    };
  };

  const navigatorControlsRef = useRef(
    new NavigationControl(goToNextIndex, goToPreviousIndex, getNavigatorStatus)
  );

  const zoomOutControlRef = useRef(
    new ZoomOutControl(() => {
      initiateMap();
    }, "Zoom out")
  );

  useEffect(() => {
    if (mapContainer.current && zoomOutControlRef.current) {
      map.current?.removeControl(zoomOutControlRef.current);

      const position = !isMobile ? "bottom-left" : "top-right";

      map.current?.addControl(zoomOutControlRef.current, position);

      zoomOutControlRef.current.setTitle("Zoom out");
    }
  }, [isMobile, map, mapContainer]);

  const initiateMap = useCallback(() => {
    const { steps, coords } = getStepsCoords(itineries);
    const { centerLocation, llb } = getCenterBoundries(coords);

    const allSame = checkCoordAllSame(coords);

    const allSameZoom = itineries?.[0]?.location?.[0]?.m_zoom_level || 5;

    setDefaultZoom(allSame ? allSameZoom : 5);

    if (!map.current) {
      const newMap = initMap({
        center: centerLocation,
        zoom: allSame ? allSameZoom : 5,
      });
      if (!newMap) return;

      newMap
        .addControl(navigatorControlsRef.current, "bottom-right")
        .addControl(
          zoomOutControlRef.current,
          !isMobile ? "bottom-left" : "top-right"
        );

      steps.forEach((step, index) => {
        if (step) {
          addStep(
            {
              latitude: step?.lat,
              longitude: step?.lng,
            },
            newMap,
            index + 1,
            indexRef.current
          );
        }
      });

      newMap.on("load", () => {
        const lines: (Feature<LineString, Properties> | null)[] = [];
        for (let i = 0; i < steps?.length; i += 1) {
          if (steps.length) {
            if (i < steps.length - 1) {
              const l = createLine(steps[i], steps[i + 1]);
              if (l) {
                lines.push(l);
              }
            } else {
              // addLine(steps[i], steps[0]);
            }
          }
        }

        const routes: any = {
          type: "FeatureCollection",
          features: lines,
        };
        newMap.addSource("route", {
          type: "geojson",
          data: routes,
        });
        newMap.addLayer({
          id: "route",
          source: "route",
          type: "line",
          paint: {
            "line-width": 3,
            "line-color": "#4F4F4F",
            "line-dasharray": [4, 4],
          },
        });
      });
    }

    if (!allSame) {
      if (llb) {
        map.current?.fitBounds(llb, { padding: 40 });
      } else {
        map.current?.setZoom(5);
      }
    }

    return () => map.current?.remove();
  }, [
    addStep,
    createLine,
    getCenterBoundries,
    getStepsCoords,
    initMap,
    isMobile,
    itineries,
    map,
  ]);

  useEffect(() => {
    initiateMap();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (itineries && !isItinerariesInitiated.current) {
      isItinerariesInitiated.current = true;
      initiateMap();
    }
  }, [initiateMap, itineries]);

  useEffect(() => {
    indexRef.current = selectedIndex;
    navigatorControlsRef.current.refresh();

    const currentZoom = map.current?.getZoom();

    if (selectedIndex !== 0 && currentZoom !== defaultZoom) {
      zoomOutControlRef.current.showButton();
    } else {
      zoomOutControlRef.current.hideButton();
    }

    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    if (selectedIndex === 0 && previousIndexRef.current !== 0) {
      initiateMap();

      previousIndexRef.current = selectedIndex;
      return;
    }

    const newLoc = itineries[selectedIndex]?.location?.[0];
    if (newLoc && !newLoc?.forceOverview) {
      map.current?.flyTo({
        center: [parseFloat(newLoc.m_lng), parseFloat(newLoc.m_lat)],
        zoom: newLoc?.m_zoom_level || 2,
      });
    } else {
      initiateMap();
      zoomOutControlRef.current.hideButton();
    }

    previousIndexRef.current = selectedIndex;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  useEffect(() => {
    markers.forEach((marker, index) => {
      const markerElement = marker.getElement();
      if (index === selectedIndex) {
        markerElement.style.zIndex = "10"; // High z-index for the selected marker
      } else {
        markerElement.style.zIndex = "1"; // Reset z-index for non-selected markers
      }

      // Update active state as well if needed
      const isActive = index === selectedIndex;
      const customMarkerElement = (
        <CustomMarker
          index={index}
          active={isActive}
          isOverview={
            itineries[selectedIndex]?.location?.[0]?.forceOverview || true
          }
        />
      );

      ReactDOM.render(customMarkerElement, markerElement);
    });
  }, [selectedIndex, markers, itineries]);

  return (
    <div className={cx(styles.root, "app-mapbox")}>
      <div
        className={cx(styles.container, "map-container")}
        ref={mapContainer}
      />
      {mapError && (
        <div className={styles.error}>
          Map preview is unavailable in this browser.
        </div>
      )}
    </div>
  );
};
export default AppMapBox;
